import React, { useContext, useEffect, useId, useMemo, useState } from 'react'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { useGetCustomGroupTypesQuery } from '../../graphql/generated'
import { Flex, HeadRow } from '../../components/Flex'
import { TitleWithSub } from '../../components/TitleWithSub'
import { Box } from '../../components/Box'
import { spacing, theme } from '../../theme'
import { SupplierGrid } from './utils/SupplierGrid'
import { InvoiceGroupGrid } from './utils/InvoiceGroupGrid'
import { FilterContext } from '../../context/FilterContext'
import { IFilterDatePeriod } from '../../utils/datePeriod'
import { captureException } from '../../utils/Sentry'
import { ErrorSnackBarContext } from '../../context/ErrorSnackBarContex'

// Utility type for tracking if the user has selected "all years",
// or some specific (calendar) year.
type YearOption =
  | {
      type: 'all'
      name: string
    }
  | {
      type: 'year'
      name: string
      period: IFilterDatePeriod
    }

export function SuppliersOrInvoiceGroup(): React.JSX.Element {
  const { setError } = useContext(ErrorSnackBarContext)

  // Label IDs for the Select:s, for accessibility purposes.
  const labellingId = useId()
  const yearLabelID = `year-${labellingId}`
  const groupLabelId = `group-${labellingId}`

  // Names of invoice group "types", which are both user-facing names,
  // and names which can be used in GQL queries for the groups.
  const { data: groups, error } = useGetCustomGroupTypesQuery()

  const { yearPeriods } = useContext(FilterContext)

  // The user can select to either display information about
  // all suppliers, or an "invoice group" they have created.
  // An example group could be "Ramavtal" (called the group "type").
  // This example "type" might then contain an item like "Catering 2020-2024",
  // which will be displayed in the grid when selecting that group.
  const groupOptions = useMemo<string[]>(() => {
    // `sort()` mutates in-place;
    // make a copy to avoid altering the array inside the GQL library.
    const sortedGroups = [...(groups?.getCustomGroupTypes ?? [])].sort()
    return [
      'Leverantörer', // default is "Suppliers"
      ...sortedGroups,
    ]
  }, [groups?.getCustomGroupTypes])
  // The current index selected in `groupOptions`
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0)

  // Year filter, used in both cases of suppliers or invoice groups.
  const yearOptions = useMemo<YearOption[]>(() => {
    // `sort()` mutates in-place;
    // make a copy to avoid altering the array for everyone using the context.
    const sortedYears = [...yearPeriods].sort(
      (a, b) => b.name.localeCompare(a.name), // latest year first
    )
    return [
      { type: 'all', name: 'Alla år' }, // Default "all years" option
      // all periods the org has data for, including partial year
      ...sortedYears.map<YearOption>((period) => {
        const name = `${period.name}${period.partialYear ? ` ${period.partialPeriodText}` : ''}`
        return { type: 'year', name, period }
      }),
    ]
  }, [yearPeriods])
  // The current index selected in `yearOptions`
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0)

  // "Type" of the selected custom invoice group, or "Leverantörer" for suppliers.
  // This is both the display name of the group, as well as the group identifier for GQL queries.
  let selectedGroupType: string = groupOptions[0] // default option is suppliers, always at zero
  if (selectedGroupIndex >= 0 && selectedGroupIndex < groupOptions.length) {
    selectedGroupType = groupOptions[selectedGroupIndex]
  }

  // Selected year is undefined for "all", or the selected year as a number.
  let selectedYear: number | undefined
  if (selectedPeriodIndex >= 0 && selectedPeriodIndex < yearOptions.length) {
    const yearOption = yearOptions[selectedPeriodIndex]
    if (yearOption.type === 'year') {
      selectedYear = Number.parseInt(yearOption.period.name, 10)
    }
  }

  useEffect(() => {
    if (error) {
      captureException(error)
      setError()
    }
  }, [error, setError])

  return (
    <>
      <Flex row stretchWidth style={{ gap: spacing.large }}>
        {/* Selector for named custom invoice groups (and "Suppliers"), by array index */}
        <FormControl variant='outlined'>
          <InputLabel id={groupLabelId}>Gruppering</InputLabel>
          <Select<number>
            label='Gruppering'
            labelId={groupLabelId}
            value={selectedGroupIndex}
            onChange={(event) => {
              // always a number, it is the index of the group option
              const index = event.target.value as number
              setSelectedGroupIndex(index)
            }}
            style={{
              minWidth: '190px',
              backgroundColor: theme.palette.background.default,
            }}
          >
            {groupOptions.map((option, index) => (
              <MenuItem key={option} value={index} data-testid='group-item'>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Selector for year, by array index */}
        <FormControl variant='outlined'>
          <InputLabel id={yearLabelID}>År</InputLabel>
          <Select<number>
            label='År'
            labelId={yearLabelID}
            value={selectedPeriodIndex}
            onChange={(event) => {
              // always a number, it is the index of the year period
              const index = event.target.value as number
              setSelectedPeriodIndex(index)
            }}
            style={{
              minWidth: '190px',
              backgroundColor: theme.palette.background.default,
            }}
          >
            {yearOptions.map((option, index) => (
              <MenuItem key={option.name} value={index} data-testid='year-item'>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Flex>
      <Box data-testid='supplier-card-box'>
        <HeadRow>
          <TitleWithSub title={selectedGroupType} sub='' />
        </HeadRow>
        {/* Index 0 is always "suppliers" */}
        {selectedGroupIndex === 0 ? (
          <SupplierGrid selectedYear={selectedYear} />
        ) : (
          <InvoiceGroupGrid
            selectedYear={selectedYear}
            selectedGroup={selectedGroupType}
          />
        )}
      </Box>
    </>
  )
}
